import { PortalModule } from '@angular/cdk/portal';
import { CommonModule } from '@angular/common';
import { NgModule } from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { NgbCollapseModule, NgbModalModule } from '@ng-bootstrap/ng-bootstrap';
import { TippyModule } from '@ngneat/helipopper';
import { EffectsModule } from '@ngrx/effects';

import { StoreModule } from '@ngrx/store';
import { CitationModule } from 'app/citation/citation.module';
import { ShareItModule } from 'app/share-it/share-it.module';

import { SharedModule } from 'common/shared.module';
import { DateFacetComponent } from 'filter-panel/components/date-facet/date-facet.component';
import { FacetFilterContainerComponent } from 'filter-panel/components/facet-filter/facet-filter-container.component';
import { FacetFilterComponent } from 'filter-panel/components/facet-filter/facet-filter.component';
import { FacetComponent } from 'filter-panel/components/facet/facet.component';
import { FilterPanelContainerComponent } from 'filter-panel/filter-panel-container.component';
import { FilterPanelComponent } from 'filter-panel/filter-panel.component';
import { FeatureToggleModule } from 'ngx-feature-toggle';
import { PERFECT_SCROLLBAR_CONFIG, PerfectScrollbarConfigInterface, PerfectScrollbarModule, } from 'ngx-perfect-scrollbar';
import { FacetExpansionPanelComponent } from 'search/components/facet-expansion-panel/facet-expansion-panel.component';
import { SearchResVersionContainer } from 'search/containers/search-res-version-container';
import { DebounceClickDirective } from 'search/directives/debounce-click.directive';
import { SearchFacetsEffects } from 'search/effects/search-facets.effects';
import { FacetMapperService } from 'search/facets/mappers/facet-mapper.service';
import { GatesService } from 'search/services/gates.service';
import { SearchMapperService } from 'search/services/mappers/search-mapper.service';
import { SearchResultService } from 'search/services/search-results.service';
import { BulkSelectModule } from '../bulk-select/bulk-select.module';
import { EntityModule } from '../entity/entity.module';
import { SavedSearchModule } from '../saved-search/saved-search.module';
import { FacetModalContentComponent } from './components/facet-modal-content/facet-modal-content.component';
import { FacetNameMapperComponent } from './components/facet-name-mapper/facet-name-mapper.component';
import { FacetsButtonComponent } from './components/facets-button/facets-button.component';
import { SearchFacetsComponent } from './components/search-facets/search-facets.component';
import { SearchResNumberComponent } from './components/search-res-number/search-res-number.component';
import { SearchResSortComponent } from './components/search-res-sort/search-res-sort.component';
import { SearchResComponent } from './components/search-res/search-res.component';
import { HistoryEffects } from './effects/history.effects';
import { SearchEffects } from './effects/search.effects';
import { FacetsService } from './facets/services/facets.service';
import { SearchRootComponent } from './pages/search-root/search-root.component';
import * as fromHistory from './reducers/history.reducer';
import * as fromSearchResults from './reducers/root.reducer';
import * as fromSearchFilterResults from './reducers/search-filter.reducer';
import { SearchResolver } from './resolvers/search.resolver';
import { SearchParamsService } from './services/search-params.service';
import { SearchService } from './services/search.service';
import { SuggestsService } from './services/suggests.service';
import { BoolQueryHelper } from './utils/bool-query-helper';
import { CatalogDateFacetComponent } from 'filter-panel/components/catalog-date-facet/catalog-date-facet.component';
import { SortMobileComponent } from './components/sort-results-modal/sort-mobile.component';
import { AdvancedSearchInstructionComponent } from './components/advanced-search-instruction/advanced-search-instruction.component';
import { ElementsV2Module } from '../elements-v2/elements-v2.module';
import { KidsCatalogToggleComponent } from 'filter-panel/components/kids-catalog-toggle/kids-catalog-toggle.component';

const COMPONENTS = [
  SearchResVersionContainer,
  SearchResComponent,
  SearchRootComponent,
  SearchResSortComponent,
  FacetComponent,
  SearchFacetsComponent,
  DateFacetComponent,
  FacetModalContentComponent,
  FacetExpansionPanelComponent,
  FacetFilterComponent,
  FacetsButtonComponent,
  FacetNameMapperComponent,
  FilterPanelComponent,
  FilterPanelContainerComponent,
  FacetFilterContainerComponent,
  SearchResNumberComponent,
  SortMobileComponent,
  AdvancedSearchInstructionComponent,
  KidsCatalogToggleComponent
];

const DEFAULT_PERFECT_SCROLLBAR_CONFIG: PerfectScrollbarConfigInterface = {
  suppressScrollX: true,
};

@NgModule({
  imports: [
    CommonModule,
    RouterModule.forChild([{
      path: '', component: SearchRootComponent, children: [
        {
          path: '',
          component: SearchResVersionContainer,
          resolve: {searchResults: SearchResolver},
          runGuardsAndResolvers: 'always',
        },
      ]
    },
      {path: 'card', loadChildren: () => import('./full-card/full-card.module').then((m) => m.FullCardModule)},
    ]),
    StoreModule.forFeature('search', fromSearchResults.reducer),
    StoreModule.forFeature('searchFilter', fromSearchFilterResults.reducer),
    StoreModule.forFeature('history', fromHistory.reducer),
    EffectsModule.forFeature([
      SearchEffects,
      SearchFacetsEffects,
      HistoryEffects,
    ]),
    ReactiveFormsModule,
    NgbCollapseModule,
    TippyModule,
    NgbModalModule,
    SharedModule,
    PerfectScrollbarModule,
    PortalModule,
    ShareItModule,
    CitationModule,
    SavedSearchModule,
    FeatureToggleModule,
    FontAwesomeModule,
    EntityModule,
    ElementsV2Module,
    BulkSelectModule,
  ],
  declarations: [
    ...COMPONENTS,
    DebounceClickDirective,
    CatalogDateFacetComponent,
    AdvancedSearchInstructionComponent,
  ],
  providers: [
    SuggestsService,
    SearchService,
    SearchResultService,
    GatesService,
    FacetsService,
    FacetMapperService,
    SearchResolver,
    BoolQueryHelper,
    SearchParamsService,
    SearchMapperService,
    {
      provide: PERFECT_SCROLLBAR_CONFIG,
      useValue: DEFAULT_PERFECT_SCROLLBAR_CONFIG,
    },
  ],
})
export class SearchModule { }
